import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { Cta } from "@/components/Cta";
import { MaskLines, Magnetic, EASE } from "@/components/motion";
import { Ribbons, Atmosphere, Sparkle } from "@/components/Art";

function Particles() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  useEffect(() => {
    const c = ref.current;
    if (!c) return undefined;
    const ctx = c.getContext("2d");
    let w = 0, h = 0, raf = 0, visible = true;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => { w = c.offsetWidth; h = c.offsetHeight; c.width = w * dpr; c.height = h * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0); };
    resize();
    const count = w < 640 ? 22 : 46;
    const ps = Array.from({ length: count }, () => ({
      x: Math.random() * w, y: Math.random() * h, r: Math.random() * 1.6 + 0.4,
      vx: (Math.random() - 0.5) * 0.12, vy: -Math.random() * 0.18 - 0.03,
      warm: Math.random() < 0.18, a: Math.random() * 0.6 + 0.2,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of ps) {
        if (!reduce) { p.x += p.vx; p.y += p.vy; if (p.y < -5) { p.y = h + 5; p.x = Math.random() * w; } }
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.warm ? `rgba(255,209,102,${p.a})` : `rgba(127,224,255,${p.a})`;
        ctx.fill();
      }
      if (!reduce && visible) raf = requestAnimationFrame(draw);
    };
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; if (visible && !reduce) { cancelAnimationFrame(raf); raf = requestAnimationFrame(draw); } });
    io.observe(c);
    draw();
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(raf); io.disconnect(); window.removeEventListener("resize", resize); };
  }, [reduce]);
  return <canvas ref={ref} className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden="true" />;
}

function EmblemStage({ mx, my }) {
  const rotX = useTransform(my, [-0.5, 0.5], [12, -12]);
  const rotY = useTransform(mx, [-0.5, 0.5], [-14, 14]);
  const glowX = useTransform(mx, [-0.5, 0.5], [-30, 30]);
  return (
    <div className="relative mx-auto aspect-square w-[min(78vw,330px)] sm:w-[400px] lg:w-[min(38vw,540px)]" style={{ perspective: 1200 }}>
      <motion.div aria-hidden="true" style={{ x: glowX }}
        className="absolute inset-[12%] rounded-full bg-[radial-gradient(circle,rgba(23,107,255,0.55),rgba(23,182,244,0.15)_45%,transparent_70%)] blur-2xl" />
      <motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.4, ease: EASE, delay: 0.2 }}
        className="absolute inset-0 rounded-full border border-si-cyan/20" aria-hidden="true">
        <div className="absolute inset-[9%] rounded-full border border-dashed border-si-line [animation:spin_60s_linear_infinite] motion-reduce:[animation:none]" />
        <div className="absolute inset-[20%] rounded-full border border-white/5 bg-white/[0.02] backdrop-blur-[2px]" />
        <span className="absolute left-1/2 top-0 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-si-cyan shadow-[0_0_20px_4px_rgba(23,182,244,0.8)]" />
        <span className="absolute bottom-[14%] right-[3%] h-2 w-2 rounded-full bg-si-orange shadow-[0_0_16px_4px_rgba(249,138,23,0.7)]" />
      </motion.div>
      <motion.div style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }} className="absolute inset-[14%]">
        <motion.img src="/brand/emblem-256.png" alt="" aria-hidden="true" width="256" height="256" fetchPriority="high"
          initial={{ opacity: 0, scale: 0.7, y: 30, filter: "blur(12px)" }} animate={{ opacity: 1, scale: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.3, ease: EASE, delay: 0.35 }}
          className="float-slow h-full w-full object-contain drop-shadow-[0_30px_60px_rgba(23,107,255,0.45)]" style={{ transform: "translateZ(60px)" }} />
      </motion.div>
      <Sparkle className="twinkle absolute right-[10%] top-[8%] h-6 w-6" />
      <Sparkle className="twinkle absolute bottom-[22%] left-[2%] h-4 w-4" color="#17B6F4" />
      {[["Ventures", "left-[-4%] top-[30%]", 0.9], ["Engineering", "right-[-6%] top-[52%]", 1.05], ["Partnerships", "left-[12%] bottom-[2%]", 1.2]].map(([t, pos, d]) => (
        <motion.span key={t} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: d, duration: 0.7, ease: EASE }}
          className={`absolute ${pos} hidden rounded-full border border-si-line bg-si-surface/60 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-si-text/90 backdrop-blur-md sm:block`}>
          {t}
        </motion.span>
      ))}
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const mx = useSpring(useMotionValue(0), { stiffness: 60, damping: 18 });
  const my = useSpring(useMotionValue(0), { stiffness: 60, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const artY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 140]);
  const artScale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 0.86]);
  const artOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.25]);
  const bgX = useTransform(mx, [-0.5, 0.5], [-24, 24]);
  const bgY = useTransform(my, [-0.5, 0.5], [-16, 16]);
  const onMove = (e) => {
    if (reduce || e.pointerType !== "mouse") return;
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };
  return (
    <section ref={ref} onPointerMove={onMove} className="relative overflow-hidden pt-28 sm:pt-32 lg:min-h-[100svh]" data-testid="hero-section">
      <Atmosphere />
      <div className="bg-grid pointer-events-none absolute inset-0" aria-hidden="true" />
      <motion.div style={{ x: bgX, y: bgY }} className="pointer-events-none absolute inset-[-4%]">
        <Ribbons id="hero" className="absolute inset-0 h-full w-full" />
      </motion.div>
      <Particles />
      <div className="wrap relative grid items-center gap-10 pb-16 lg:min-h-[calc(100svh-8rem)] lg:grid-cols-12 lg:gap-6 lg:pb-24">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
            className="eyebrow mb-7 flex flex-wrap items-center gap-3" data-testid="hero-eyebrow">
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-si-cyan opacity-60 motion-reduce:animate-none" /><span className="relative inline-flex h-2 w-2 rounded-full bg-si-cyan" /></span>
            Independent venture studio · Product engineering
          </motion.p>
          <MaskLines as="h1" onLoad delay={0.15} testId="hero-headline" className="h-display"
            lines={["Big ideas", "deserve to", <span key="r" className="inline-flex items-baseline gap-3">become <span className="text-fusion pr-2 italic font-extrabold">real.</span></span>]} />
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8, ease: EASE }}
            className="mt-8 max-w-xl text-lg leading-relaxed text-si-muted sm:text-xl" data-testid="hero-subtext">
            We build our own software ventures and help ambitious founders turn ideas into intelligent, scalable products.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.8, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-3">
            <Magnetic><Cta to="/contact" size="lg" testId="hero-cta-start" trackId="hero-start-project">Start a Project</Cta></Magnetic>
            <Cta to="/ventures" variant="secondary" size="lg" testId="hero-cta-ventures" trackId="hero-ventures">Explore Our Ventures</Cta>
            <Link to="/how-we-build" className="ml-2 text-[15px] font-semibold text-si-muted underline decoration-si-line underline-offset-8 transition-colors hover:text-si-text hover:decoration-si-cyan" data-testid="hero-link-how">How We Build</Link>
          </motion.div>
        </div>
        <motion.div style={{ y: artY, scale: artScale, opacity: artOpacity }} className="lg:col-span-5">
          <EmblemStage mx={mx} my={my} />
        </motion.div>
      </div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
        className="wrap relative hidden items-center justify-between pb-8 font-mono text-[11px] uppercase tracking-[0.2em] text-si-muted lg:flex">
        <span className="flex items-center gap-2"><ArrowDown className="h-3.5 w-3.5 animate-bounce motion-reduce:animate-none" />Scroll to explore</span>
        <span>Nautis · DemoVexa · Qikod · Zingura</span>
      </motion.div>
    </section>
  );
}
